import { ArrowLeft, Send, Search, Filter, AlertCircle } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { usePayments } from '@/hooks/useCpn'
import { authService } from '@/services/auth'
import { getPaymentStatusInfo } from '@/utils/cpn'
import { getErrorMessage } from '@/utils/errorFormatting'
import { formatDate } from '@/utils/payment'

interface PaymentListScreenProps {
  onBack: () => void
  onViewPaymentDetails?: (paymentId: string) => void
}

const PaymentListScreen = ({
  onBack,
  onViewPaymentDetails,
}: PaymentListScreenProps) => {
  const userId = authService.getUserId()
  const [showActiveOnly, setShowActiveOnly] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  // Fetch all payments using TanStack Query hook
  const {
    data: paymentsResponse,
    isLoading,
    error,
  } = usePayments(userId || '', showActiveOnly ? true : undefined, !!userId)
  const payments = paymentsResponse?.payments || []

  // Filter payments based on search term
  const filteredPayments = payments.filter((payment) => {
    if (!searchTerm) return true

    const searchLower = searchTerm.toLowerCase()
    const statusInfo = getPaymentStatusInfo(payment.status)

    return (
      payment.paymentUuid.toLowerCase().includes(searchLower) ||
      (payment.intentId &&
        payment.intentId.toLowerCase().includes(searchLower)) ||
      payment.fromCurrency.toLowerCase().includes(searchLower) ||
      payment.toCurrency.toLowerCase().includes(searchLower) ||
      payment.status.toLowerCase().includes(searchLower) ||
      statusInfo.label.toLowerCase().includes(searchLower) ||
      payment.fromAmount.toString().includes(searchLower) ||
      payment.toAmount.toString().includes(searchLower)
    )
  })

  // Calculate summary stats - only count successful transactions
  const totalAmount = payments
    .filter((payment) => payment.status.toUpperCase() === 'COMPLETED')
    .reduce((sum, payment) => sum + payment.fromAmount, 0)
  const completedPayments = payments.filter(
    (p) => p.status.toUpperCase() === 'COMPLETED'
  ).length
  const pendingPayments = payments.filter((p) =>
    ['CRYPTO_FUNDS_PENDING', 'FIAT_PAYMENT_INITIATED', 'CREATED'].includes(
      p.status.toUpperCase()
    )
  ).length

  const handleViewDetails = (paymentId: string) => {
    if (onViewPaymentDetails) {
      onViewPaymentDetails(paymentId)
    }
  }

  // Helper function to check if error is "no payments data available"
  const isNoPaymentsError = (error: unknown) => {
    const err = error as { response?: { data?: string } } | undefined
    const data = err?.response?.data
    return (
      data === 'No payments data available' ||
      (typeof data === 'string' && data.includes('No payments data available'))
    )
  }

  return (
    <div className="h-[600px] flex flex-col overflow-hidden bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-white hover:bg-white/20 p-1"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-heading font-bold">All Payments</h1>
              <p className="text-blue-100 text-sm">
                {isLoading
                  ? 'Loading...'
                  : `${filteredPayments.length} transactions found`}
              </p>
            </div>
          </div>
          {/* <div className="flex space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchTransactionsByEmail}
              className="text-white hover:bg-white/20"
              title="Fetch transactions by email"
            >
              <Search className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20"
            >
              <Download className="w-4 h-4" />
            </Button>
          </div> */}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-white/10 rounded-lg p-2">
            <p className="text-xs text-blue-200">Total Sent</p>
            <p className="text-sm font-semibold">
              $
              {totalAmount.toLocaleString('en-CA', {
                minimumFractionDigits: 2,
              })}
            </p>
          </div>
          <div className="bg-white/10 rounded-lg p-2">
            <p className="text-xs text-blue-200">Completed</p>
            <p className="text-sm font-semibold">{completedPayments}</p>
          </div>
          <div className="bg-white/10 rounded-lg p-2">
            <p className="text-xs text-blue-200">Pending</p>
            <p className="text-sm font-semibold">{pendingPayments}</p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="p-4 border-b bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
            <input
              type="text"
              placeholder="Search payments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>
          <Button
            variant={showActiveOnly ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowActiveOnly(!showActiveOnly)}
            className="flex items-center space-x-1"
          >
            <Filter className="w-4 h-4" />
            <span className="text-xs">Active</span>
          </Button>
        </div>
      </div>

      {/* Payment List */}
      <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-900">
        {/* Loading State */}
        {isLoading && (
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Card
                key={i}
                className="p-4 animate-pulse bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20 mb-2"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Error State - but not for "no payments data available" */}
        {error && !isNoPaymentsError(error) && (
          <div className="p-4">
            <Card className="p-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
              <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">
                  {getErrorMessage(error) || 'Failed to load payments'}
                </span>
              </div>
            </Card>
          </div>
        )}

        {/* Payment Items */}
        {!isLoading && (!error || isNoPaymentsError(error)) && (
          <div className="p-4 space-y-3">
            {filteredPayments.map((payment) => {
              const statusInfo = getPaymentStatusInfo(payment.status)

              return (
                <Card
                  key={payment.paymentUuid}
                  className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer border-l-4 border-l-transparent hover:border-l-blue-400 dark:hover:border-l-blue-500 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                  onClick={() => handleViewDetails(payment.intentId)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                        <Send className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-1">
                          CAD → {payment.toCurrency}
                        </h4>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          <span>{formatDate(payment.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.color} border mb-1`}
                      >
                        {statusInfo.label}
                      </span>
                      <div className="text-xs text-gray-600 dark:text-gray-400 font-mono">
                        {payment.paymentUuid.slice(0, 8)}...
                      </div>
                    </div>
                  </div>

                  {/* Additional Details Row */}
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>
                      Receiving: {payment.toAmount.toFixed(2)}{' '}
                      {payment.toCurrency}
                    </span>
                  </div>
                </Card>
              )
            })}

            {/* Empty State */}
            {filteredPayments.length === 0 &&
              !isLoading &&
              (!error || isNoPaymentsError(error)) && (
                <Card className="p-8 text-center bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                  <Send className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    {searchTerm ? 'No matching payments' : 'No payments found'}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {searchTerm
                      ? 'Try adjusting your search terms'
                      : 'Start your first international transfer today'}
                  </p>
                  {!searchTerm && (
                    <Button
                      onClick={onBack}
                      className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700"
                    >
                      Start New Transfer
                    </Button>
                  )}
                </Card>
              )}
          </div>
        )}
      </div>
    </div>
  )
}

export default PaymentListScreen
